import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
    Box,
    Button,
    useTheme
} from '@mui/material';
import {
    AccountCircle,
    VolunteerActivism,
    BugReport,
} from '@mui/icons-material';
import { useFeedbackModal } from '../../../contexts/FeedbackModalContext';
import { ThemeToggleMenu } from './ThemeToggleMenu';

function getUserButtonStyles(showTransparent, isDark) {
    const defaultColor = isDark ? 'primary.light' : 'primary.main';
    const hoverBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';

    return {
        ml: 1,
        fontWeight: 700,
        fontSize: '0.825rem',
        py: 0.5,
        px: 1.25,
        whiteSpace: 'nowrap',
        color: showTransparent ? 'white' : defaultColor,
        '& .MuiButton-startIcon': { mr: 0.5 },
        '&:hover': {
            bgcolor: showTransparent ? 'rgba(255,255,255,0.1)' : hoverBg,
        }
    };
}

function getReportIssueStyles(showTransparent, isDark) {
    const hoverColor = isDark ? 'primary.light' : 'primary.main';
    const hoverBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';

    return {
        ml: 1,
        fontWeight: 700,
        fontSize: '0.825rem',
        py: 0.5,
        px: 1.25,
        whiteSpace: 'nowrap',
        color: showTransparent ? 'white' : 'text.secondary',
        '& .MuiButton-startIcon': { mr: 0.5 },
        '&:hover': {
            color: showTransparent ? 'white' : hoverColor,
            bgcolor: showTransparent ? 'rgba(255,255,255,0.1)' : hoverBg,
        }
    };
}

/**
 * UserMenu Component
 * Renders desktop user actions: Bug report, My Dashboard / Login, Donate button, and Theme Toggle Menu.
 */
export function UserMenu({ user, showTransparent }) {
    const theme = useTheme();
    const { openFeedbackModal } = useFeedbackModal();
    const isDark = theme.palette.mode === 'dark';
    const userButtonSx = getUserButtonStyles(showTransparent, isDark);
    const reportIssueSx = getReportIssueStyles(showTransparent, isDark);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Auth Button */}
            {user ? (
                <>
                    <Button
                        onClick={() => openFeedbackModal('bug')}
                        startIcon={<BugReport sx={{ fontSize: '0.95rem !important' }} />}
                        aria-label="Report Issue"
                        sx={reportIssueSx}
                    >
                        Report Issue
                    </Button>
                    <Button
                        component={Link}
                        to="/dashboard"
                        startIcon={<AccountCircle />}
                        sx={userButtonSx}
                    >
                        My Dashboard
                    </Button>
                </>
            ) : (
                <Button
                    component={Link}
                    to="/login"
                    startIcon={<AccountCircle />}
                    sx={userButtonSx}
                >
                    Login
                </Button>
            )}

            {/* Donate button */}
            <Button
                component={Link}
                to="/donate"
                variant="contained"
                color="secondary"
                startIcon={<VolunteerActivism />}
                sx={{
                    ml: 1,
                    fontWeight: 700,
                    fontSize: '0.825rem',
                    py: 0.5,
                    px: 1.5,
                    '& .MuiButton-startIcon': { mr: 0.5 },
                    boxShadow: showTransparent ? '0 4px 14px rgba(0,0,0,0.4)' : 2,
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: showTransparent ? '0 6px 20px rgba(0,0,0,0.6)' : 4,
                    },
                    transition: 'all 0.2s ease'
                }}
            >
                Donate
            </Button>

            {/* Theme Toggle Menu (Issue #796) */}
            <ThemeToggleMenu showTransparent={showTransparent} />
        </Box>
    );
}

UserMenu.propTypes = {
    user: PropTypes.object,
    showTransparent: PropTypes.bool,
};

export default UserMenu;
